import React, { useCallback, useMemo } from 'react';
import { Image } from 'react-native';
import type { BestAvailableOrderProps, EventOrderStatProps, EventProps, MarketProps, MarketSideOptionProps, OrderProps, TradeProps } from '../../../types';
import { MarketButtonHelpers } from '../MarketButton/api';
import MarketButton from '../MarketButton';
import { Icons, LinearDiagnal } from '../../../Components';
import { TeamEventMarketHelpers } from '../TeamEventMarket/api';
import { MarketComponentHelpers } from '../../api';
import { Button, Text, View } from '../../../Components/Themed';
import { useColors } from '../../../constants/useColors';

type EventMarketProps = {
    event:EventProps,
    width?:number,
    market:MarketProps,
    float?:boolean,
    disabled_markets?:string[],
    hide_market_banner?:boolean,
    latest_trades:TradeProps[],
    hide_liquidity?:boolean,
    style?:any,
    highlight_router_orders?:boolean,
    direction?:'horizontal'|'vertical',
    best_available_orders:BestAvailableOrderProps[],
    event_order_stats:EventOrderStatProps[],
    show_grades?:boolean,
    default_price_view:'last_trade'|'best_available',
    onOrder:(order:OrderProps) => void,
    onView:(data: { event_id:string, event_type:string, market_id:string }) => void,
    onTradeLongPress:(trade:TradeProps) => void
}

const EventMarket = ({ event, float, style, highlight_router_orders, disabled_markets, direction, hide_liquidity, market, latest_trades, best_available_orders, hide_market_banner, event_order_stats, show_grades, default_price_view, onOrder, onView, onTradeLongPress }:EventMarketProps) => {
    const C = useColors();
    const { liquidity, open_order_count } = useMemo(() => MarketComponentHelpers.getLiqudity(event_order_stats),[JSON.stringify(event_order_stats)]);

    const is_locked = useMemo(() => MarketComponentHelpers.isEventMarketLocked(event, market.market_id),[event.last_update_datetime]);
    
    const renderSides = useCallback((data:{ item:MarketSideOptionProps, index:number, market:MarketProps }) => {
        const ba_order = best_available_orders.find(o => o.market_id == market.market_id && o.side == data.item.side && o.show);
        const side_details = MarketComponentHelpers.getTeamEventSideIds(event, market, data.item, undefined, undefined);
        if(!side_details){ return }
        let trade = MarketButtonHelpers.getVisibleTrade(default_price_view, data.market, data.item, latest_trades, best_available_orders, side_details.side_id, side_details.reversed_side_id); 
        if(!trade){
            trade = MarketButtonHelpers.getTradeFromMarket(market, event.event_id, 'team', data.item.side, data.item.id_source, side_details.side_id, side_details.reversed_side_id);
        }
        if(!trade){ return <></> }
        let side_label = event[data.item.side as keyof EventProps]?.abbr
        let disabled = false
        if(disabled_markets && disabled_markets.includes(data.market.market_id.toString())){ disabled = true }

        return (
            <View key={`${event.event_id}_${market.market_id}_${data.item.side}`}  style={{ margin:2, height:50, width:75 }}>
                <MarketButton
                    side_option={data.item}
                    market={data.market}
                    side_label={side_label}
                    show_grades={show_grades}
                    highlight_router_orders={highlight_router_orders}
                    locked={is_locked || disabled}
                    order={ba_order}
                    onLongPress={onTradeLongPress}
                    trade={trade}
                    onPress={(order) => {
                        const order_title = MarketComponentHelpers.getOrderTitleForTeamEvent(order, market, event);
                        if(!order_title){ return alert('Unable to process order') }
                        onOrder({ ...order, title: order_title, league_id: event.league_id })
                    }}
                />
            </View>
        )
    },[JSON.stringify(best_available_orders), market.market_id, JSON.stringify(disabled_markets), default_price_view])
    return (
        <View float={float} style={[{ borderRadius:8 }, style]}>
            {!hide_market_banner ?
             <LinearDiagnal 
                label={market.stat_label ?? market.type}
                label_size={14}
                style={{ borderTopLeftRadius:8, padding:5 }}
                left_color={C.text.gold}
                right_color={'#ebd197'}
            />
            :<></>}
            <View transparent style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:1 }}>

                <View type='body' transparent style={{ flexDirection:direction == 'horizontal' ? 'row' : 'column', flexGrow:2, padding:0, justifyContent:'center', alignItems:'center' }}>
                    {direction != 'horizontal' ?
                    <View transparent style={{flexDirection:'row', alignItems:'center'}}>
                        <Image
                            source={{ uri: event.away?.image?.url }}
                            style={{ height:50, width:50, marginRight:5 }}
                            resizeMode='cover'
                        />
                        <Text theme='description'>VS</Text>
                        <Image
                            source={{ uri: event.home?.image?.url }}
                            style={{ height:50, width:50, marginLeft:5 }}
                            resizeMode='cover'
                        />
                    </View>
                    :<></>}
                    <View transparent style={{ padding:10, paddingBottom:0 }}>
                        <Text weight='bold' theme='h2' style={{ maxWidth:100 }}  textAlign='center'>{event.event_title}</Text>
                        <Text style={{ marginTop:3 }} theme='light' textAlign='center' size={12}>{TeamEventMarketHelpers.getTimeDetail(event)}</Text>
                    </View>
                </View>
                <View transparent nativeID='market_sides' style={{ flexDirection:'row', flexGrow:1, justifyContent:'flex-end', alignItems:'center', padding:10 }}>
                    {market.side_options.sort((a,b) => {
                        let a_t = a.side == market.trade_side ? 1 : 0
                        let b_t = b.side == market.trade_side ? 1 : 0
                        return a_t - b_t
                    }).map((so, index) => {
                        return renderSides({ item:so, index, market })
                    })}
                    
                </View>
            </View>
            {!hide_liquidity ?
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', borderBottomLeftRadius:8, borderBottomRightRadius:8 }}>
                <Button transparent style={{ flexGrow:1, flexDirection:'row', padding:10 }} onPress={() => {
                    onView({
                        event_id: event.event_id, 
                        event_type: 'team', 
                        market_id: market.market_id
                    });
                }}>
                    <View float style={{ height:30, width:30, borderRadius:100, justifyContent:'center', alignItems:'center' }}>
                        <Icons.LiquidityIcon color={C.text.h1} size={30}/>
                    </View>
                    <View transparent style={{ marginLeft:10 }}>
                        <Text theme='description'>Liquidity</Text>
                        <Text size={12} style={{ marginTop:3 }} color={C.text.highlight} weight='semibold'>${liquidity.toFixed()} {open_order_count} Orders</Text>
                    </View>
                </Button>
            </View>
            :<></>}
        </View>
    )
}

export default React.memo(EventMarket, (left, right) => {
    if(JSON.stringify(left) == JSON.stringify(right)){ return true }
    return false
})